import {
  AreaType,
  AtGradeExpectedIntxTypes,
  CrashSeverity,
  CrashSeverityFiOrTotal,
  PennDotDistricts,
  SafetyFacilityType,
  SafetyIntxType,
  UrbanSubIntxTypes,
} from "./SPFConstants";

export const getKValueForRoundabout = (
  crashSeverity: CrashSeverity,
  isUrban: boolean
) => {
  return (
    isUrban
      ? {
          [CrashSeverity.TOTAL]: 0.503,
          [CrashSeverity.FATAL_INJURY]: 0.329,
          [CrashSeverity.PDO]: 0.6064,
        }
      : {
          [CrashSeverity.TOTAL]: 0.6292,
          [CrashSeverity.FATAL_INJURY]: 0.4424,
          [CrashSeverity.PDO]: 0.7284,
        }
  )[crashSeverity];
};

const getTwscUrbanSubArtKValue = (
  crashSeverity: CrashSeverityFiOrTotal,
  pennDotDistrict: PennDotDistricts
) => {
  switch (pennDotDistrict) {
    case PennDotDistricts.DISTRICT_1:
      return crashSeverity === CrashSeverity.TOTAL ? 0.286 : 0.0000057;
    case PennDotDistricts.DISTRICT_2:
      return crashSeverity === CrashSeverity.TOTAL ? 0.286 : 0.0000057;
    case PennDotDistricts.DISTRICT_3:
      return crashSeverity === CrashSeverity.TOTAL ? 0.193 : 0.119;
    case PennDotDistricts.DISTRICT_4:
      return crashSeverity === CrashSeverity.TOTAL ? 0.166 : 0.049;
    case PennDotDistricts.DISTRICT_5:
      return crashSeverity === CrashSeverity.TOTAL ? 0.342 : 0.406;
    case PennDotDistricts.DISTRICT_6:
      return crashSeverity === CrashSeverity.TOTAL ? 0.397 : 0.449;
    case PennDotDistricts.DISTRICT_8:
      return crashSeverity === CrashSeverity.TOTAL ? 0.272 : 0.263;
    case PennDotDistricts.DISTRICT_9:
      return crashSeverity === CrashSeverity.TOTAL ? 0.482 : 0.456;
    case PennDotDistricts.DISTRICT_10:
      return crashSeverity === CrashSeverity.TOTAL ? 0.482 : 0.456;
    case PennDotDistricts.DISTRICT_11:
      return crashSeverity === CrashSeverity.TOTAL ? 0.407 : 0.452;
    case PennDotDistricts.DISTRICT_12:
      return crashSeverity === CrashSeverity.TOTAL ? 0.44 : 0.364;
    default:
      throw new Error(`Count not find k value for UrbanSubArt TWSC`);
  }
};

/**
 * @returns the overdispersion parameter k for all non roundabout, at grade intersection types
 */
export const getKValue = (
  facilityType: SafetyFacilityType,
  intxType: SafetyIntxType,
  crashSeverity: CrashSeverityFiOrTotal,
  pennDotDistrict: PennDotDistricts
): number => {
  const getUrban3LegSignalKValue = () => {
    return crashSeverity === CrashSeverity.TOTAL ? 0.385 : 0.458;
  };
  if (facilityType === SafetyFacilityType.TWO_LANE_RURAL_ROADWAY) {
    switch (intxType) {
      case SafetyIntxType.THREE_LEG_MINOR_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 1.117 : 1.81;
      case SafetyIntxType.THREE_LEG_SIGNAL:
        return crashSeverity === CrashSeverity.TOTAL ? 0.982 : 1.114;
      case SafetyIntxType.FOUR_LEG_MINOR_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 1.348 : 2.597;
      case SafetyIntxType.THREE_LEG_ALL_STOP:
      case SafetyIntxType.FOUR_LEG_ALL_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 1.283 : 1.522;
      case SafetyIntxType.FOUR_LEG_SIGNAL:
        return crashSeverity === CrashSeverity.TOTAL ? 0.579 : 0.892;
    }
  }

  if (facilityType === SafetyFacilityType.RURAL_MULTILANE_HIGHWAY) {
    switch (intxType) {
      case SafetyIntxType.THREE_LEG_MINOR_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 0.187 : 0.441;
      case SafetyIntxType.FOUR_LEG_MINOR_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 0.381 : 0.413;
      case SafetyIntxType.THREE_LEG_SIGNAL:
      case SafetyIntxType.FOUR_LEG_SIGNAL:
        return crashSeverity === CrashSeverity.TOTAL ? 0.203 : 0.227;
    }
  }

  if (facilityType === SafetyFacilityType.URBAN_SUBURBAN_COLLECTOR) {
    switch (intxType) {
      case SafetyIntxType.THREE_LEG_ALL_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 0.576 : 0.145;
      case SafetyIntxType.FOUR_LEG_MINOR_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 0.442 : 0.638;
      case SafetyIntxType.FOUR_LEG_ALL_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 0.306 : 0.084;
      case SafetyIntxType.THREE_LEG_SIGNAL:
        return getUrban3LegSignalKValue();
      case SafetyIntxType.FOUR_LEG_SIGNAL:
        return crashSeverity === CrashSeverity.TOTAL ? 0.188 : 0.243;
      case SafetyIntxType.THREE_LEG_MINOR_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 0.454 : 0.496;
    }
  }

  if (facilityType === SafetyFacilityType.URBAN_SUBURBAN_ARTERIAL) {
    switch (intxType) {
      case SafetyIntxType.THREE_LEG_MINOR_STOP:
        return getTwscUrbanSubArtKValue(crashSeverity, pennDotDistrict);
      case SafetyIntxType.FOUR_LEG_MINOR_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 0.387 : 0.368;
      case SafetyIntxType.THREE_LEG_SIGNAL:
        return getUrban3LegSignalKValue();
      case SafetyIntxType.FOUR_LEG_SIGNAL:
        return crashSeverity === CrashSeverity.TOTAL ? 0.356 : 0.432;
      case SafetyIntxType.THREE_LEG_ALL_STOP:
      case SafetyIntxType.FOUR_LEG_ALL_STOP:
        return crashSeverity === CrashSeverity.TOTAL ? 0.387 : 0.368;
    }
  }

  throw new Error(
    `Cannot find k value for ${facilityType} ${intxType} ${crashSeverity} ${pennDotDistrict}`
  );
};

export const getKValueForSignalOrStopRampTerminal = (
  crashSeverity: CrashSeverity.FATAL_INJURY | CrashSeverity.PDO,
  intxType:
    | SafetyIntxType.DIAMOND_WITH_SIGNALS
    | SafetyIntxType.DIAMOND_WITH_STOP_CONTROL
): number => {
  if (intxType === SafetyIntxType.DIAMOND_WITH_SIGNALS) {
    return crashSeverity === CrashSeverity.FATAL_INJURY ? 1 / 11.5 : 1 / 7.21;
  }
  if (intxType === SafetyIntxType.DIAMOND_WITH_STOP_CONTROL) {
    return crashSeverity === CrashSeverity.FATAL_INJURY ? 1 / 2.58 : 1 / 4.27;
  }
  throw new Error(`Cannot find k value for signal/stop ramp terminal`);
};

const CalibrationFactorforThreeLegSignal = {
  [PennDotDistricts.DISTRICT_1]: { total: 1.0, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_2]: { total: 1.0, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_3]: { total: 0.87, fatalInjury: 0.81 },
  [PennDotDistricts.DISTRICT_4]: { total: 1.0, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_5]: { total: 1.18, fatalInjury: 1.12 },
  [PennDotDistricts.DISTRICT_6]: { total: 1.0, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_8]: { total: 0.87, fatalInjury: 0.81 },
  [PennDotDistricts.DISTRICT_9]: { total: 1.0, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_10]: { total: 1.0, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_11]: { total: 1.18, fatalInjury: 1.12 },
  [PennDotDistricts.DISTRICT_12]: { total: 1.0, fatalInjury: 1.0 },
};
export const getUrbanThreeLegSignalSpfs = () => {
  return {
    getTotalPredicted: ({
      majorRoad,
      minorRoad,
      pennDotDistrict,
    }: UrbanSubIntxTypes): number =>
      Math.exp(-5.113) *
      Math.pow(majorRoad.aadt, 0.393) *
      Math.pow(minorRoad.aadt, 0.219) *
      Math.exp(0.097 * Number(majorRoad.hasExclusiveLeft)) *
      Math.exp(0.11 * Number(minorRoad.hasExclusiveLeft)) *
      Math.exp(
        0.131 * Number(majorRoad.speedLimit >= 30 && majorRoad.speedLimit <= 35)
      ) *
      Math.exp(0.346 * Number(majorRoad.speedLimit >= 40)) *
      CalibrationFactorforThreeLegSignal[pennDotDistrict].total,
    getFatalInjuryPredicted: ({
      majorRoad,
      minorRoad,
      pennDotDistrict,
    }: UrbanSubIntxTypes): number =>
      Math.exp(-5.677) *
      Math.pow(majorRoad.aadt, 0.381) *
      Math.pow(minorRoad.aadt, 0.247) *
      Math.exp(0.115 * Number(majorRoad.hasExclusiveLeft)) *
      Math.exp(0.181 * Number(majorRoad.speedLimit >= 40)) *
      CalibrationFactorforThreeLegSignal[pennDotDistrict].fatalInjury,
  };
};

interface getExpectedCrashesProps {
  k: number;
  predictedCrashes: number;
  yearsOfAnalysis: number;
  observedCrashes: number;
}

export const getNumberExpectedCrashes = ({
  k,
  predictedCrashes,
  yearsOfAnalysis,
  observedCrashes,
}: getExpectedCrashesProps): number => {
  const w = 1 / (1 + k * yearsOfAnalysis * predictedCrashes);
  return w * predictedCrashes + (1 - w) * (observedCrashes / yearsOfAnalysis);
};

type expectedCrashesPropsAtGrade = AtGradeExpectedIntxTypes & {
  crashSeverity: CrashSeverityFiOrTotal;
  getPredictedCrashes: (props: any) => number;
};

/**
 * this function called for at grade intersection SPFs only. Ramp terminal SPFs call getNumberExpectedCrashes() directly
 */
export const getExpectedCrashes = (
  props: expectedCrashesPropsAtGrade
): number => {
  const {
    crashSeverity,
    facilityType,
    getPredictedCrashes,
    intxType,
    observedFatalInjuryCrashes,
    observedTotalCrashes,
    pennDotDistrict,
    yearsOfAnalysis,
  } = props;
  const isRoundabout =
    intxType === SafetyIntxType.THREE_LEG_ROUNDABOUT ||
    intxType === SafetyIntxType.FOUR_LEG_ROUNDABOUT;
  const isUrban =
    facilityType === SafetyFacilityType.URBAN_SUBURBAN_ARTERIAL ||
    facilityType === SafetyFacilityType.URBAN_SUBURBAN_COLLECTOR;
  return getNumberExpectedCrashes({
    k: isRoundabout
      ? getKValueForRoundabout(crashSeverity, isUrban)
      : getKValue(facilityType, intxType, crashSeverity, pennDotDistrict),
    predictedCrashes: getPredictedCrashes(props),
    yearsOfAnalysis,
    observedCrashes:
      crashSeverity === CrashSeverity.FATAL_INJURY
        ? observedFatalInjuryCrashes
        : observedTotalCrashes,
  });
};

export const getFatalInjuryCrashRatio = ({
  fiInitial,
  pdoInitial,
  totalCrashes,
}: {
  fiInitial: number;
  pdoInitial: number;
  totalCrashes: number;
}): number => (fiInitial / (pdoInitial + fiInitial)) * totalCrashes;

export const getaadtxrd = ({ aadtin, aadtout }: any): number => {
  return 0.5 * (aadtin + aadtout);
};

export const pEx = ({ aadtin, aadtout, aadtex, aadten }: any): number => {
  return aadtex / (aadtin + aadtout + aadtex + aadten);
};

export const ROUNDABOUT_CALIBRATION_FACTOR = 0.53;

interface RoundaboutRoads {
  majorRoad: { aadt: number };
  minorRoad: { aadt: number };
}
export interface RoundaboutSpfs {
  getTotalPredicted: (p: any) => number;
  getFatalInjuryPredicted: (p: any) => number;
  getPDOPredicted: (p: any) => number;
}
export const get3LegRoundaboutSPFs = (areaType: AreaType): RoundaboutSpfs => {
  return areaType === AreaType.URBAN
    ? {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
        }: RoundaboutRoads): number =>
          Math.exp(-5.6049) *
          Math.pow(majorRoad.aadt, 0.3274) *
          Math.pow(minorRoad.aadt, 0.396) *
          Math.exp(-0.8681) *
          ROUNDABOUT_CALIBRATION_FACTOR,
        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
        }: RoundaboutRoads): number =>
          Math.exp(-8.6597) *
          Math.pow(majorRoad.aadt, 0.5271) *
          Math.pow(minorRoad.aadt, 0.3505) *
          Math.exp(-0.7317),
        getPDOPredicted: ({ majorRoad, minorRoad }: RoundaboutRoads): number =>
          Math.exp(-5.5319) *
          Math.pow(majorRoad.aadt, 0.2653) *
          Math.pow(minorRoad.aadt, 0.4294) *
          Math.exp(-0.926),
      }
    : {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
        }: RoundaboutRoads): number =>
          Math.exp(-5.3299) *
          Math.pow(majorRoad.aadt, 0.3356) *
          Math.pow(minorRoad.aadt, 0.5142) *
          Math.exp(-1.6229) *
          ROUNDABOUT_CALIBRATION_FACTOR,
        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
        }: RoundaboutRoads): number =>
          Math.exp(-10.4848) *
          Math.pow(majorRoad.aadt, 0.7756) *
          Math.pow(minorRoad.aadt, 0.4239) *
          Math.exp(-1.5586),
        getPDOPredicted: ({ majorRoad, minorRoad }: RoundaboutRoads): number =>
          Math.exp(-5.4115) *
          Math.pow(majorRoad.aadt, 0.298) *
          Math.pow(minorRoad.aadt, 0.5463) *
          Math.exp(-1.7296),
      };
};

export const get4LegRoundaboutSPFs = (areaType: AreaType): RoundaboutSpfs =>
  areaType === AreaType.URBAN
    ? {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
        }: RoundaboutRoads): number =>
          Math.exp(-5.6049) *
          Math.pow(majorRoad.aadt, 0.3274) *
          Math.pow(minorRoad.aadt, 0.396) *
          ROUNDABOUT_CALIBRATION_FACTOR,
        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
        }: RoundaboutRoads): number =>
          Math.exp(-8.6597) *
          Math.pow(majorRoad.aadt, 0.5271) *
          Math.pow(minorRoad.aadt, 0.3505),
        getPDOPredicted: ({ majorRoad, minorRoad }: RoundaboutRoads): number =>
          Math.exp(-5.5319) *
          Math.pow(majorRoad.aadt, 0.2653) *
          Math.pow(minorRoad.aadt, 0.4294),
      }
    : {
        getTotalPredicted: ({ majorRoad, minorRoad }: RoundaboutRoads) =>
          Math.exp(-5.3299) *
          Math.pow(majorRoad.aadt, 0.3356) *
          Math.pow(minorRoad.aadt, 0.5142) *
          Math.exp(-0.9375) *
          ROUNDABOUT_CALIBRATION_FACTOR,
        getFatalInjuryPredicted: ({ majorRoad, minorRoad }: RoundaboutRoads) =>
          Math.exp(-10.4848) *
          Math.pow(majorRoad.aadt, 0.7756) *
          Math.pow(minorRoad.aadt, 0.4239) *
          Math.exp(-0.5506),
        getPDOPredicted: ({ majorRoad, minorRoad }: RoundaboutRoads) =>
          Math.exp(-5.4115) *
          Math.pow(majorRoad.aadt, 0.298) *
          Math.pow(minorRoad.aadt, 0.5463) *
          Math.exp(-1.0192),
      };
